/**
 * AdminMediaIntel — iter-435b admin surface for the SupoClip-inspired
 * pipeline in `routes/fwtv_intel.py`.
 *
 * Tabs:
 *   • Pending review   — approve/reject with inline preview
 *   • All clips        — list, filter by status, jump to any state
 *   • Social queue     — see queued/sent rows across all platforms
 *
 * Every action calls into APIs that already exist and are e2e-tested.
 */
import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import {
  Sparkles, Play, CheckCircle2, XCircle, RefreshCw, Film, Rocket,
  Languages, ListChecks, Send, X, KeyRound, Image as ImageIcon,
  Activity, AlertTriangle,
} from 'lucide-react';
import { getToken } from '@/lib/authToken';
import { SocialCredentialsPanel } from '@/components/media/SocialCredentialsPanel';

const API = process.env.REACT_APP_BACKEND_URL;
const authH = () => ({ Authorization: `Bearer ${getToken()}` });

const STATUS_STYLES = {
  draft:          { label: 'Draft',          cls: 'border-slate-600 text-slate-300' },
  pending_review: { label: 'Pending',        cls: 'border-amber-400/40 text-amber-300 bg-amber-500/10' },
  approved:       { label: 'Approved',       cls: 'border-emerald-400/40 text-emerald-300 bg-emerald-500/10' },
  rejected:       { label: 'Rejected',       cls: 'border-rose-400/40 text-rose-300 bg-rose-500/10' },
  published:      { label: 'Published',      cls: 'border-indigo-400/40 text-indigo-300 bg-indigo-500/10' },
};

const PLATFORMS = ['twitter', 'youtube_shorts', 'instagram_reels', 'facebook_reels', 'tiktok'];


export default function AdminMediaIntel() {
  const [tab, setTab] = useState('pending');
  return (
    <div className="min-h-screen bg-slate-950 text-slate-200 p-6">
      <div className="max-w-6xl mx-auto space-y-4">
        <header className="flex flex-wrap items-center gap-3">
          <Sparkles className="h-7 w-7 text-indigo-400" />
          <div className="flex-1">
            <h1 className="text-2xl font-semibold text-white">Media Intelligence</h1>
            <p className="text-sm text-slate-400">
              AI-graded highlights → real FFmpeg clips (9:16 / 1:1 / 16:9) → human approval → publish to FWTV VOD + `embed.js` snippets + social queue.
            </p>
          </div>
        </header>

        <Tabs value={tab} onValueChange={setTab} className="w-full">
          <TabsList className="bg-slate-900 border border-slate-800" data-testid="mi-tabs">
            <TabsTrigger value="pending" data-testid="mi-tab-pending">
              <ListChecks className="h-4 w-4 mr-1" /> Pending review
            </TabsTrigger>
            <TabsTrigger value="social" data-testid="mi-tab-social">
              <Send className="h-4 w-4 mr-1" /> Social queue
            </TabsTrigger>
            <TabsTrigger value="rank" data-testid="mi-tab-rank">
              <Sparkles className="h-4 w-4 mr-1" /> AI hook titles
            </TabsTrigger>
            <TabsTrigger value="creds" data-testid="mi-tab-creds">
              <KeyRound className="h-4 w-4 mr-1" /> Social credentials
            </TabsTrigger>
            <TabsTrigger value="stats" data-testid="mi-tab-stats">
              <Activity className="h-4 w-4 mr-1" /> Drain stats
            </TabsTrigger>
          </TabsList>

          <TabsContent value="pending" className="mt-4"><PendingQueue /></TabsContent>
          <TabsContent value="social"  className="mt-4"><SocialQueue /></TabsContent>
          <TabsContent value="rank"    className="mt-4"><HookTitleGenerator /></TabsContent>
          <TabsContent value="creds"   className="mt-4"><SocialCredentialsPanel /></TabsContent>
          <TabsContent value="stats"   className="mt-4"><DrainStatsPanel /></TabsContent>
        </Tabs>
      </div>
    </div>
  );
}


// ============================================================================
// Pending review
// ============================================================================
function PendingQueue() {
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);
  const [selected, setSelected] = useState(null);
  const [renderOpen, setRenderOpen] = useState(false);
  const [publishOpen, setPublishOpen] = useState(false);
  const [socialOpen, setSocialOpen] = useState(false);
  const [rejectOpen, setRejectOpen] = useState(false);
  const [quoteOpen, setQuoteOpen] = useState(false);

  const load = async () => {
    setBusy(true);
    try {
      const r = await axios.get(`${API}/api/fwtv/admin/clips/pending`, { headers: authH() });
      setRows(r.data.clips || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load pending queue');
    } finally { setBusy(false); }
  };
  useEffect(() => { load(); }, []);

  const approve = async (clip) => {
    try {
      await axios.post(`${API}/api/fwtv/admin/clips/${clip.id}/approve`, {}, { headers: authH() });
      toast.success('Approved');
      setRows(rs => rs.filter(x => x.id !== clip.id));
    } catch (e) { toast.error(e?.response?.data?.detail || 'Approve failed'); }
  };

  return (
    <>
      <Card className="bg-slate-900/60 border-slate-800">
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-sm">Pending review ({rows.length})</CardTitle>
          <Button size="sm" variant="outline" onClick={load}
                    className="border-slate-700 text-slate-300 hover:bg-slate-800"
                    data-testid="mi-refresh-btn">
            <RefreshCw className={`h-3 w-3 mr-1 ${busy ? 'animate-spin' : ''}`} />Refresh
          </Button>
        </CardHeader>
        <CardContent className="space-y-2">
          {!busy && !rows.length && (
            <div className="py-8 text-center text-sm text-slate-500">
              No clips awaiting review. Submit clips from the Sermons page to start.
            </div>
          )}
          {rows.map(c => (
            <ClipRow
              key={c.id}
              clip={c}
              onApprove={() => approve(c)}
              onReject={() => { setSelected(c); setRejectOpen(true); }}
              onRender={() => { setSelected(c); setRenderOpen(true); }}
              onPreview={() => setSelected(c)}
            />
          ))}
        </CardContent>
      </Card>

      {selected && (
        <ClipPreview clip={selected} onClose={() => setSelected(null)}
                       onPublish={() => setPublishOpen(true)}
                       onSocial={() => setSocialOpen(true)}
                       onQuote={() => setQuoteOpen(true)} />
      )}

      <RenderDialog open={renderOpen} clip={selected} onClose={() => setRenderOpen(false)}
                       onDone={(newClip) => setSelected(newClip)} />
      <RejectDialog open={rejectOpen} clip={selected} onClose={() => setRejectOpen(false)}
                       onDone={() => { setRejectOpen(false); setRows(rs => rs.filter(x => x.id !== selected?.id)); }} />
      <PublishDialog open={publishOpen} clip={selected} onClose={() => setPublishOpen(false)}
                        onDone={() => { setPublishOpen(false); load(); }} />
      <SocialQueueDialog open={socialOpen} clip={selected} onClose={() => setSocialOpen(false)} />
      <QuoteGraphicDialog open={quoteOpen} clip={selected} onClose={() => setQuoteOpen(false)} />
    </>
  );
}


function ClipRow({ clip, onApprove, onReject, onRender, onPreview }) {
  const st = STATUS_STYLES[clip.approval_status || 'draft'] || STATUS_STYLES.draft;
  const renders = Object.keys(clip.renders || {});
  const dur = Math.round((clip.end_seconds || 0) - (clip.start_seconds || 0));
  return (
    <div className="border border-slate-800 rounded-lg p-3 flex items-center gap-3 bg-slate-800/30"
          data-testid={`mi-clip-row-${clip.id}`}>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium text-slate-100 truncate">
          {clip.title || 'Untitled clip'}
        </div>
        <div className="text-xs text-slate-500 mt-0.5">
          {dur}s · start {(clip.start_seconds || 0).toFixed(1)}s · sermon <code className="text-slate-400">{clip.sermon_id?.slice(-6)}</code>
        </div>
        <div className="flex gap-1 mt-1 flex-wrap">
          <Badge variant="outline" className={`text-[10px] ${st.cls}`}>{st.label}</Badge>
          {renders.map(a => (
            <Badge key={a} variant="outline"
                    className="text-[10px] border-slate-700 text-slate-300">
              {a} ✓
            </Badge>
          ))}
          {!renders.length && (
            <Badge variant="outline" className="text-[10px] border-slate-700 text-slate-500">
              no renders yet
            </Badge>
          )}
        </div>
      </div>
      <div className="flex items-center gap-1 shrink-0">
        <Button size="sm" variant="outline" onClick={onPreview}
                 className="h-8 border-slate-700 text-slate-300 hover:bg-slate-800"
                 data-testid={`mi-preview-${clip.id}`}>
          <Play className="h-3 w-3" />
        </Button>
        <Button size="sm" variant="outline" onClick={onRender}
                 className="h-8 border-slate-700 text-slate-300 hover:bg-slate-800"
                 data-testid={`mi-render-${clip.id}`}>
          <Film className="h-3 w-3 mr-1" />Render
        </Button>
        <Button size="sm" onClick={onApprove}
                 className="h-8 bg-emerald-600 hover:bg-emerald-500 text-white"
                 disabled={!renders.length}
                 data-testid={`mi-approve-${clip.id}`}>
          <CheckCircle2 className="h-3 w-3 mr-1" />Approve
        </Button>
        <Button size="sm" variant="destructive" onClick={onReject}
                 className="h-8" data-testid={`mi-reject-${clip.id}`}>
          <XCircle className="h-3 w-3" />
        </Button>
      </div>
    </div>
  );
}


function ClipPreview({ clip, onClose, onPublish, onSocial, onQuote }) {
  const aspects = Object.keys(clip.renders || {});
  const [aspect, setAspect] = useState(aspects[0] || '9:16');
  useEffect(() => { if (aspects.length && !aspects.includes(aspect)) setAspect(aspects[0]); },
             [clip?.id]); // eslint-disable-line

  const src = clip.approval_status === 'published'
    ? `${API}/api/fwtv/embed/clip/${clip.id}.mp4`
    // Authenticated download endpoint — passing token via query when the video element requests
    // isn't safely doable, so preview only works for published clips OR we render inline via auth-blob.
    : null;

  return (
    <Dialog open={!!clip} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-slate-950 border-slate-800 text-slate-200 max-w-2xl">
        <DialogHeader>
          <DialogTitle>{clip.title || 'Untitled clip'}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          {aspects.length > 0 && (
            <div className="flex items-center gap-2">
              <Label className="text-xs text-slate-400">Aspect:</Label>
              <Select value={aspect} onValueChange={setAspect}>
                <SelectTrigger className="w-32 bg-slate-900 border-slate-800 text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {aspects.map(a => <SelectItem key={a} value={a}>{a}</SelectItem>)}
                </SelectContent>
              </Select>
              {clip.approval_status !== 'published' && (
                <span className="text-[11px] text-slate-500">
                  (preview requires publish — use the download button below for pre-publish review)
                </span>
              )}
            </div>
          )}
          {src ? (
            <video controls playsinline className="w-full max-h-[60vh] bg-black rounded-md"
                    data-testid="mi-preview-video">
              <source src={src} type="video/mp4" />
            </video>
          ) : aspects.length ? (
            <a href={`${API}/api/fwtv/admin/clips/${clip.id}/download?aspect=${aspect}`}
                target="_blank" rel="noreferrer"
                className="inline-flex items-center gap-1 text-xs text-indigo-300 hover:underline"
                data-testid="mi-download-preview">
              Download {aspect} render →
            </a>
          ) : (
            <div className="text-sm text-slate-500 p-6 text-center border border-dashed border-slate-800 rounded-md">
              No renders yet — hit <strong>Render</strong> on the row above.
            </div>
          )}
        </div>
        <DialogFooter>
          {clip.approval_status === 'approved' && (
            <Button size="sm" onClick={onPublish} data-testid="mi-publish-btn">
              <Rocket className="h-4 w-4 mr-1" />Publish
            </Button>
          )}
          {(clip.approval_status === 'approved' || clip.approval_status === 'published') && aspects.length > 0 && (
            <Button size="sm" variant="outline" onClick={onSocial}
                     className="border-slate-700 text-slate-300 hover:bg-slate-800"
                     data-testid="mi-social-btn">
              <Send className="h-4 w-4 mr-1" />Queue for social
            </Button>
          )}
          <Button size="sm" variant="outline" onClick={onQuote}
                    className="border-slate-700 text-slate-300 hover:bg-slate-800"
                    data-testid="mi-quote-btn">
            <ImageIcon className="h-4 w-4 mr-1" />Quote graphic
          </Button>
          <Button size="sm" variant="outline" onClick={onClose}
                    className="border-slate-700 text-slate-300 hover:bg-slate-800">Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


function RenderDialog({ open, clip, onClose, onDone }) {
  const [aspects, setAspects] = useState(['9:16']);
  const [burn, setBurn] = useState(true);
  const [busy, setBusy] = useState(false);

  const toggle = (a) => setAspects(xs => xs.includes(a) ? xs.filter(x => x !== a) : [...xs, a]);

  const submit = async () => {
    if (!clip || !aspects.length) return;
    setBusy(true);
    try {
      const r = await axios.post(
        `${API}/api/fwtv/admin/clips/${clip.id}/render`,
        { aspects, burn_captions: burn },
        { headers: authH() },
      );
      if (r.data.success) {
        toast.success(`Rendered: ${aspects.join(', ')}`);
        onDone({ ...clip, renders: r.data.renders });
      } else {
        toast.error(`Some aspects failed: ${Object.keys(r.data.errors || {}).join(', ')}`);
      }
      onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Render failed'); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-slate-950 border-slate-800 text-slate-200">
        <DialogHeader><DialogTitle>Render clip {clip?.id?.slice(-6)}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs text-slate-400">Aspect ratios</Label>
            <div className="flex gap-2 mt-1">
              {['16:9', '9:16', '1:1'].map(a => (
                <Button
                  key={a} size="sm"
                  variant={aspects.includes(a) ? 'default' : 'outline'}
                  onClick={() => toggle(a)}
                  className={aspects.includes(a) ? '' : 'border-slate-700 text-slate-300 hover:bg-slate-800'}
                  data-testid={`mi-aspect-${a}`}
                >{a}</Button>
              ))}
            </div>
          </div>
          <label className="flex items-center gap-2 text-xs text-slate-300"
                   data-testid="mi-burn-captions">
            <input type="checkbox" checked={burn} onChange={e => setBurn(e.target.checked)} />
            Burn captions from transcript
          </label>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}
                    className="border-slate-700 text-slate-300 hover:bg-slate-800">Cancel</Button>
          <Button onClick={submit} disabled={busy || !aspects.length}
                    data-testid="mi-render-submit">
            {busy ? 'Rendering…' : 'Start render'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


function RejectDialog({ open, clip, onClose, onDone }) {
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (reason.trim().length < 3) { toast.error('Reason needs at least 3 characters'); return; }
    setBusy(true);
    try {
      await axios.post(`${API}/api/fwtv/admin/clips/${clip.id}/reject`,
                          { reason }, { headers: authH() });
      toast.success('Rejected');
      onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Reject failed'); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-slate-950 border-slate-800 text-slate-200">
        <DialogHeader><DialogTitle>Reject clip</DialogTitle></DialogHeader>
        <div className="space-y-2">
          <Label className="text-xs text-slate-400">Reason (visible in audit)</Label>
          <Textarea value={reason} onChange={e => setReason(e.target.value)}
                       rows={3} className="bg-slate-900 border-slate-800"
                       data-testid="mi-reject-reason" />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}
                    className="border-slate-700 text-slate-300 hover:bg-slate-800">Cancel</Button>
          <Button variant="destructive" onClick={submit} disabled={busy}
                    data-testid="mi-reject-submit">
            {busy ? 'Rejecting…' : 'Reject'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


function PublishDialog({ open, clip, onClose, onDone }) {
  const aspects = Object.keys(clip?.renders || {});
  const [aspect, setAspect] = useState(aspects[0] || '9:16');
  const [tags, setTags] = useState('');
  const [busy, setBusy] = useState(false);
  const [embed, setEmbed] = useState(null);

  const submit = async () => {
    setBusy(true);
    try {
      const r = await axios.post(
        `${API}/api/fwtv/admin/clips/${clip.id}/publish`,
        { aspect, tags: tags.split(',').map(t => t.trim()).filter(Boolean) },
        { headers: authH() },
      );
      setEmbed(r.data.embed);
      toast.success('Published');
    } catch (e) { toast.error(e?.response?.data?.detail || 'Publish failed'); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-slate-950 border-slate-800 text-slate-200">
        <DialogHeader><DialogTitle>Publish clip to FWTV + embed snippets</DialogTitle></DialogHeader>
        {!embed ? (
          <>
            <div className="space-y-3">
              <div>
                <Label className="text-xs text-slate-400">Which render becomes the featured version?</Label>
                <Select value={aspect} onValueChange={setAspect}>
                  <SelectTrigger className="bg-slate-900 border-slate-800 mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {aspects.map(a => <SelectItem key={a} value={a}>{a}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs text-slate-400">Tags (comma-separated)</Label>
                <Input value={tags} onChange={e => setTags(e.target.value)}
                          placeholder="sermon-highlight, grace"
                          className="bg-slate-900 border-slate-800"
                          data-testid="mi-publish-tags" />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={onClose}
                        className="border-slate-700 text-slate-300 hover:bg-slate-800">Cancel</Button>
              <Button onClick={submit} disabled={busy || !aspects.length}
                        data-testid="mi-publish-submit">
                <Rocket className="h-4 w-4 mr-1" />
                {busy ? 'Publishing…' : 'Publish'}
              </Button>
            </DialogFooter>
          </>
        ) : (
          <div className="space-y-2 text-sm">
            <div className="text-emerald-300">✔ Published — copy any of these into your one.com pages:</div>
            {['iframe', 'embed_js_marker', 'json_url'].map(k => (
              <div key={k}>
                <div className="text-[10px] uppercase text-slate-500 tracking-wider">{k.replace('_', ' ')}</div>
                <code className="block text-[11px] bg-slate-900 border border-slate-800 rounded p-2 break-all"
                        data-testid={`mi-embed-${k}`}>
                  {embed[k]}
                </code>
              </div>
            ))}
            <DialogFooter>
              <Button onClick={() => { onDone(); onClose(); }}>Done</Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}


function SocialQueueDialog({ open, clip, onClose }) {
  const aspects = Object.keys(clip?.renders || {});
  const [aspect, setAspect] = useState(aspects[0] || '9:16');
  const [platforms, setPlatforms] = useState(['twitter']);
  const [caption, setCaption] = useState('');
  const [hashtags, setHashtags] = useState('');
  const [busy, setBusy] = useState(false);

  const toggle = (p) => setPlatforms(xs => xs.includes(p) ? xs.filter(x => x !== p) : [...xs, p]);

  const submit = async () => {
    if (!platforms.length) { toast.error('Pick at least one platform'); return; }
    setBusy(true);
    try {
      await axios.post(
        `${API}/api/fwtv/admin/clips/${clip.id}/queue-social`,
        { platforms, aspect, caption,
           hashtags: hashtags.split(/[\s,]+/).filter(Boolean) },
        { headers: authH() },
      );
      toast.success(`Queued for ${platforms.length} platform(s)`);
      onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Queue failed'); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-slate-950 border-slate-800 text-slate-200">
        <DialogHeader><DialogTitle>Queue for social publish</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs text-slate-400">Platforms</Label>
            <div className="flex gap-1 flex-wrap mt-1">
              {PLATFORMS.map(p => (
                <Button
                  key={p} size="sm"
                  variant={platforms.includes(p) ? 'default' : 'outline'}
                  className={platforms.includes(p) ? '' : 'border-slate-700 text-slate-300 hover:bg-slate-800'}
                  onClick={() => toggle(p)}
                  data-testid={`mi-platform-${p}`}
                >{p.replace('_', ' ')}</Button>
              ))}
            </div>
          </div>
          <div>
            <Label className="text-xs text-slate-400">Aspect</Label>
            <Select value={aspect} onValueChange={setAspect}>
              <SelectTrigger className="bg-slate-900 border-slate-800 mt-1"><SelectValue /></SelectTrigger>
              <SelectContent>
                {aspects.map(a => <SelectItem key={a} value={a}>{a}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs text-slate-400">Caption</Label>
            <Textarea value={caption} onChange={e => setCaption(e.target.value)} rows={3}
                       className="bg-slate-900 border-slate-800"
                       data-testid="mi-social-caption" />
          </div>
          <div>
            <Label className="text-xs text-slate-400">Hashtags (space or comma separated)</Label>
            <Input value={hashtags} onChange={e => setHashtags(e.target.value)}
                    placeholder="#faith #sermon"
                    className="bg-slate-900 border-slate-800"
                    data-testid="mi-social-hashtags" />
          </div>
          <p className="text-[10px] text-slate-500">
            OAuth adapters are not wired yet — items will sit in <strong>queued</strong> state until per-tenant Twitter/YT/IG credentials are configured. Nothing gets posted without your explicit drain action.
          </p>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}
                    className="border-slate-700 text-slate-300 hover:bg-slate-800">Cancel</Button>
          <Button onClick={submit} disabled={busy} data-testid="mi-social-submit">
            {busy ? 'Queuing…' : 'Queue'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


// ============================================================================
// Social queue tab
// ============================================================================
function SocialQueue() {
  const [rows, setRows] = useState([]);
  const [counts, setCounts] = useState({});
  const [busy, setBusy] = useState(false);
  const [filter, setFilter] = useState('');

  const load = async () => {
    setBusy(true);
    try {
      const r = await axios.get(
        `${API}/api/fwtv/admin/social-queue${filter ? `?status=${filter}` : ''}`,
        { headers: authH() },
      );
      setRows(r.data.queue || []);
      setCounts(r.data.counts || {});
    } catch (e) { toast.error(e?.response?.data?.detail || 'Load failed'); }
    finally { setBusy(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter]);

  const drain = async (ids, force) => {
    try {
      const r = await axios.post(`${API}/api/fwtv/admin/social-queue/drain`,
                                    { ids, force_send: force },
                                    { headers: authH() });
      toast.success(force ? 'Marked sent (stub)' : 'Drain attempted');
      load();
      return r.data;
    } catch (e) { toast.error(e?.response?.data?.detail || 'Drain failed'); }
  };

  const cancel = async (id) => {
    try {
      await axios.post(`${API}/api/fwtv/admin/social-queue/${id}/cancel`, {},
                          { headers: authH() });
      toast.success('Cancelled');
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Cancel failed'); }
  };

  return (
    <Card className="bg-slate-900/60 border-slate-800">
      <CardHeader className="flex flex-row items-center justify-between gap-2">
        <div className="flex items-center gap-2 flex-wrap">
          <CardTitle className="text-sm">Social publish queue</CardTitle>
          {['queued', 'sent', 'failed', 'cancelled'].map(s => (
            <Badge key={s} variant="outline"
                    onClick={() => setFilter(filter === s ? '' : s)}
                    className={`cursor-pointer text-[10px] ${filter === s ? 'border-indigo-400/60 text-indigo-300 bg-indigo-500/10' : 'border-slate-700 text-slate-400'}`}
                    data-testid={`mi-social-filter-${s}`}>
              {s} · {counts[s] || 0}
            </Badge>
          ))}
        </div>
        <Button size="sm" variant="outline" onClick={load}
                  className="border-slate-700 text-slate-300 hover:bg-slate-800"
                  data-testid="mi-social-refresh">
          <RefreshCw className={`h-3 w-3 mr-1 ${busy ? 'animate-spin' : ''}`} />Refresh
        </Button>
      </CardHeader>
      <CardContent className="space-y-2">
        {!busy && !rows.length && (
          <div className="py-8 text-center text-sm text-slate-500">
            Queue empty. Approve + publish a clip, then hit "Queue for social" to add rows.
          </div>
        )}
        {rows.map(row => (
          <div key={row.id}
                className="border border-slate-800 rounded-lg p-3 bg-slate-800/30 flex items-center gap-3"
                data-testid={`mi-social-row-${row.id}`}>
            <div className="flex-1 min-w-0">
              <div className="text-sm text-slate-100 truncate">{row.clip_title || 'Untitled'}</div>
              <div className="text-xs text-slate-500 mt-0.5">
                {row.platform} · {row.aspect} · queued {row.queued_at ? new Date(row.queued_at).toLocaleString() : ''}
              </div>
              {row.caption && <div className="text-xs text-slate-400 mt-1 truncate">{row.caption}</div>}
            </div>
            <Badge variant="outline" className={`text-[10px] ${
              row.status === 'sent' ? 'border-emerald-400/40 text-emerald-300'
                : row.status === 'failed' ? 'border-rose-400/40 text-rose-300'
                : row.status === 'cancelled' ? 'border-slate-600 text-slate-400'
                : 'border-amber-400/40 text-amber-300'
            }`}>{row.status}</Badge>
            {row.status === 'queued' && (
              <>
                <Button size="sm" variant="outline"
                         className="h-8 border-slate-700 text-slate-300 hover:bg-slate-800"
                         onClick={() => drain([row.id], true)}
                         data-testid={`mi-force-send-${row.id}`}>Force-send (stub)</Button>
                <Button size="sm" variant="outline"
                         className="h-8 border-slate-700 text-slate-300 hover:bg-slate-800"
                         onClick={() => cancel(row.id)}
                         data-testid={`mi-cancel-${row.id}`}>
                  <X className="h-3 w-3" />
                </Button>
              </>
            )}
          </div>
        ))}
      </CardContent>
    </Card>
  );
}


// ============================================================================
// AI hook-title generator
// ============================================================================
function HookTitleGenerator() {
  const [sermonId, setSermonId] = useState('');
  const [topN, setTopN] = useState(6);
  const [contextHint, setContextHint] = useState('sermon');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const run = async () => {
    if (!sermonId.trim()) { toast.error('Enter a sermon id first'); return; }
    setBusy(true); setResult(null);
    try {
      const r = await axios.post(
        `${API}/api/fwtv/admin/sermons/${sermonId.trim()}/highlights/rank-llm`,
        { top_n: topN, context_hint: contextHint },
        { headers: authH() },
      );
      setResult(r.data);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Rank failed'); }
    finally { setBusy(false); }
  };

  return (
    <Card className="bg-slate-900/60 border-slate-800">
      <CardHeader>
        <CardTitle className="text-sm">AI hook-title generator</CardTitle>
        <p className="text-xs text-slate-500 mt-1">
          Feed a transcript to Claude Sonnet, get back ranked candidates
          with virality scores + suggested hook titles. Copy-paste into
          the create-clip form once you like a candidate.
        </p>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="grid sm:grid-cols-3 gap-3">
          <div className="sm:col-span-2">
            <Label className="text-xs text-slate-400">Sermon id</Label>
            <Input value={sermonId} onChange={e => setSermonId(e.target.value)}
                    placeholder="_id from /api/fwtv/admin/sermons"
                    className="bg-slate-900 border-slate-800 font-mono text-xs"
                    data-testid="mi-rank-sermon-id" />
          </div>
          <div>
            <Label className="text-xs text-slate-400">Top N</Label>
            <Input type="number" value={topN} min={1} max={20}
                    onChange={e => setTopN(parseInt(e.target.value || '6', 10))}
                    className="bg-slate-900 border-slate-800"
                    data-testid="mi-rank-top-n" />
          </div>
        </div>
        <div>
          <Label className="text-xs text-slate-400">Context (helps the LLM tune tone)</Label>
          <Select value={contextHint} onValueChange={setContextHint}>
            <SelectTrigger className="bg-slate-900 border-slate-800 mt-1"><SelectValue /></SelectTrigger>
            <SelectContent>
              {['sermon', 'lecture', 'testimony', 'conference-talk', 'fundraising-appeal'].map(x =>
                <SelectItem key={x} value={x}>{x}</SelectItem>
              )}
            </SelectContent>
          </Select>
        </div>
        <Button onClick={run} disabled={busy} data-testid="mi-rank-submit">
          <Sparkles className="h-4 w-4 mr-1" />
          {busy ? 'Ranking…' : 'Rank highlights'}
        </Button>

        {result && (
          <div className="mt-3 space-y-2">
            <div className="text-xs text-slate-500">
              Backend: <strong>{result.backend}</strong> · {result.clips?.length || 0} candidates
            </div>
            {(result.clips || []).map((c, i) => (
              <div key={i} className="border border-slate-800 rounded-lg p-3 bg-slate-800/30"
                    data-testid={`mi-rank-candidate-${i}`}>
                <div className="flex items-center gap-2">
                  <span className="text-lg font-semibold text-indigo-300">{c.score ?? '?'}</span>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm text-slate-100 font-medium">{c.title || '—'}</div>
                    <div className="text-xs text-slate-500">
                      {(c.start ?? 0).toFixed(1)}s → {(c.end ?? 0).toFixed(1)}s
                      ({((c.end ?? 0) - (c.start ?? 0)).toFixed(1)}s)
                    </div>
                  </div>
                  <Button size="sm" variant="outline"
                           className="border-slate-700 text-slate-300 hover:bg-slate-800"
                           onClick={() => {
                             navigator.clipboard?.writeText(c.title || '');
                             toast.success('Title copied');
                           }}
                           data-testid={`mi-rank-copy-${i}`}>Copy title</Button>
                </div>
                {c.quote && <div className="text-xs text-slate-400 mt-2 italic">"{c.quote}"</div>}
                {c.reason && <div className="text-[10px] text-slate-500 mt-1">{c.reason}</div>}
                {c.sub_scores && (
                  <div className="flex gap-2 mt-2 text-[10px]">
                    {Object.entries(c.sub_scores).map(([k, v]) => (
                      <span key={k} className="px-1.5 py-0.5 rounded border border-slate-700 text-slate-400">
                        {k}: {v}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}


// ============================================================================
// Quote graphic dialog
// ============================================================================
function QuoteGraphicDialog({ open, clip, onClose }) {
  const [quote, setQuote] = useState('');
  const [attribution, setAttribution] = useState('');
  const [reference, setReference] = useState('');
  const [aspect, setAspect] = useState('1:1');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const submit = async () => {
    if (quote.trim().length < 5) { toast.error('Quote too short'); return; }
    setBusy(true); setResult(null);
    try {
      const r = await axios.post(
        `${API}/api/fwtv/admin/clips/${clip.id}/quote-graphic`,
        { quote: quote.trim(), attribution: attribution.trim() || undefined,
           reference: reference.trim() || undefined, aspect },
        { headers: authH() },
      );
      setResult(r.data.graphic);
      toast.success('Rendered');
    } catch (e) { toast.error(e?.response?.data?.detail || 'Render failed'); }
    finally { setBusy(false); }
  };

  const downloadUrl = result
    ? `${API}/api/fwtv/admin/clips/${clip.id}/quote-graphic/${result.filename}`
    : null;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-slate-950 border-slate-800 text-slate-200 max-w-2xl">
        <DialogHeader><DialogTitle>Generate branded quote graphic</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs text-slate-400">Quote</Label>
            <Textarea value={quote} onChange={e => setQuote(e.target.value)}
                       rows={3} className="bg-slate-900 border-slate-800"
                       placeholder='"For God so loved the world…"'
                       data-testid="mi-quote-text" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs text-slate-400">Attribution (optional)</Label>
              <Input value={attribution} onChange={e => setAttribution(e.target.value)}
                      placeholder="Pastor Name"
                      className="bg-slate-900 border-slate-800"
                      data-testid="mi-quote-attribution" />
            </div>
            <div>
              <Label className="text-xs text-slate-400">Reference (optional)</Label>
              <Input value={reference} onChange={e => setReference(e.target.value)}
                      placeholder="John 3:16"
                      className="bg-slate-900 border-slate-800"
                      data-testid="mi-quote-reference" />
            </div>
          </div>
          <div>
            <Label className="text-xs text-slate-400">Aspect</Label>
            <div className="flex gap-2 mt-1">
              {['1:1', '9:16', '16:9'].map(a => (
                <Button key={a} size="sm"
                         variant={aspect === a ? 'default' : 'outline'}
                         onClick={() => setAspect(a)}
                         className={aspect === a ? '' : 'border-slate-700 text-slate-300 hover:bg-slate-800'}
                         data-testid={`mi-quote-aspect-${a}`}>{a}</Button>
              ))}
            </div>
          </div>
          {result && (
            <div className="border border-slate-800 rounded-lg p-3 bg-slate-800/30">
              <div className="text-xs text-emerald-300 mb-2">✔ Rendered {result.aspect} ({result.w}×{result.h}, {Math.round(result.filesize/1024)} KB)</div>
              <img src={downloadUrl}
                    alt="preview"
                    className="max-h-64 rounded border border-slate-700 mx-auto"
                    data-testid="mi-quote-preview-img" />
              <a href={downloadUrl} download
                  className="inline-flex items-center gap-1 text-xs text-indigo-300 hover:underline mt-2"
                  data-testid="mi-quote-download">
                Download PNG →
              </a>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}
                    className="border-slate-700 text-slate-300 hover:bg-slate-800">Close</Button>
          <Button onClick={submit} disabled={busy}
                    data-testid="mi-quote-submit">
            <ImageIcon className="h-4 w-4 mr-1" />
            {busy ? 'Rendering…' : 'Render graphic'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}



// ============================================================================
// Drain stats panel — per-platform health monitor for social publish drains
// ============================================================================
const HEALTH_STYLES = {
  healthy:  { label: 'Healthy',  cls: 'border-emerald-400/40 text-emerald-300 bg-emerald-500/10' },
  degraded: { label: 'Degraded', cls: 'border-amber-400/40 text-amber-300 bg-amber-500/10' },
  critical: { label: 'Critical', cls: 'border-rose-400/40 text-rose-300 bg-rose-500/10' },
  idle:     { label: 'Idle',     cls: 'border-slate-600 text-slate-400' },
};

function DrainStatsPanel() {
  const [weeks, setWeeks] = useState(4);
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setBusy(true);
    try {
      const r = await axios.get(
        `${API}/api/fwtv/admin/media-intel/drain-stats?weeks=${weeks}`,
        { headers: authH() },
      );
      setData(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load drain stats');
    } finally { setBusy(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [weeks]);

  const t = data?.totals || {};
  const attempted = (t.sent || 0) + (t.failed || 0);
  const successPct = attempted ? Math.round((t.sent / attempted) * 100) : null;

  return (
    <div data-testid="mi-stats-panel" className="space-y-3">
      <Card className="bg-slate-900/60 border-slate-800">
        <CardHeader className="flex flex-row items-center justify-between gap-2">
          <div>
            <CardTitle className="text-sm">Social drain stats</CardTitle>
            <p className="text-xs text-slate-500 mt-1">
              Per-platform publish health. Watch for platforms flagged{' '}
              <span className="text-rose-300">stale creds</span> — that&apos;s a token expiry signal.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Select value={String(weeks)} onValueChange={(v) => setWeeks(parseInt(v, 10))}>
              <SelectTrigger className="w-28 bg-slate-900 border-slate-800 text-xs"
                                data-testid="mi-stats-weeks">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {[1, 2, 4, 8, 12].map(w => (
                  <SelectItem key={w} value={String(w)}>{w} week{w > 1 ? 's' : ''}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button size="sm" variant="outline" onClick={load}
                      className="border-slate-700 text-slate-300 hover:bg-slate-800"
                      data-testid="mi-stats-refresh">
              <RefreshCw className={`h-3 w-3 mr-1 ${busy ? 'animate-spin' : ''}`} />Refresh
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
            <KpiTile label="Total" value={t.total || 0} />
            <KpiTile label="Sent" value={t.sent || 0} tone="good" />
            <KpiTile label="Failed" value={t.failed || 0} tone="danger" />
            <KpiTile label="Queued" value={t.queued || 0} tone="warn" />
            <KpiTile label="Success rate"
                       value={successPct === null ? '—' : `${successPct}%`}
                       tone={successPct === null ? 'default'
                              : successPct >= 90 ? 'good'
                              : successPct >= 50 ? 'warn' : 'danger'} />
          </div>
        </CardContent>
      </Card>

      {!data?.platforms?.length && (
        <div className="text-sm text-slate-500 text-center py-6">Loading…</div>
      )}
      {data?.platforms?.map(p => (
        <PlatformStatRow key={p.platform} p={p} />
      ))}
    </div>
  );
}


function KpiTile({ label, value, tone = 'default' }) {
  const tc = {
    default: 'text-white', good: 'text-emerald-300',
    warn: 'text-amber-300', danger: 'text-rose-300',
  }[tone];
  return (
    <div className="border border-slate-800 rounded-md p-3 bg-slate-800/30">
      <div className="text-[10px] uppercase tracking-wider text-slate-500">{label}</div>
      <div className={`text-2xl font-semibold mt-1 ${tc}`}>{value}</div>
    </div>
  );
}


function PlatformStatRow({ p }) {
  const st = HEALTH_STYLES[p.health] || HEALTH_STYLES.idle;
  const attempted = (p.sent || 0) + (p.failed || 0);
  const rate = attempted ? Math.round((p.sent / attempted) * 100) : null;
  const maxWk = Math.max(1, ...(p.weeks || []).map(w => (w.sent || 0) + (w.failed || 0)));

  return (
    <Card className="bg-slate-900/60 border-slate-800"
            data-testid={`mi-stats-row-${p.platform}`}>
      <CardContent className="p-3">
        <div className="flex items-center gap-3 flex-wrap">
          <div className="text-sm font-medium text-slate-100 flex-1 min-w-0 sm:min-w-[140px]">
            {p.platform.replace(/_/g, ' ')}
          </div>
          <Badge variant="outline" className={`text-[10px] ${st.cls}`}
                   data-testid={`mi-stats-health-${p.platform}`}>
            {st.label}
          </Badge>
          {p.stale_credentials_suspected && (
            <Badge variant="outline"
                     className="text-[10px] border-rose-400/40 text-rose-300 bg-rose-500/10"
                     data-testid={`mi-stats-stale-${p.platform}`}>
              <AlertTriangle className="h-3 w-3 mr-1" />stale creds
            </Badge>
          )}
          <div className="flex gap-3 text-[11px] text-slate-400">
            <span>Sent: <strong className="text-emerald-300">{p.sent}</strong></span>
            <span>Failed: <strong className="text-rose-300">{p.failed}</strong></span>
            <span>Queued: <strong className="text-amber-300">{p.queued}</strong></span>
            <span>Rate: <strong className="text-slate-100">{rate === null ? '—' : `${rate}%`}</strong></span>
          </div>
        </div>
        {(p.weeks && p.weeks.length > 0) && (
          <div className="flex items-end gap-1 mt-2 h-10">
            {p.weeks.map(w => {
              const total = (w.sent || 0) + (w.failed || 0);
              const h = Math.round(((total || 0) / maxWk) * 100);
              return (
                <div key={w.iso_week} className="flex-1 min-w-[8px] flex flex-col items-center gap-1"
                       title={`${w.iso_week} — sent ${w.sent || 0}, failed ${w.failed || 0}`}>
                  <div className="w-full flex flex-col justify-end" style={{ height: '32px' }}>
                    <div className="w-full bg-rose-500/50 rounded-t"
                         style={{ height: `${Math.round(((w.failed || 0) / (total || 1)) * h)}%` }} />
                    <div className="w-full bg-emerald-500/60"
                         style={{ height: `${Math.round(((w.sent || 0) / (total || 1)) * h)}%` }} />
                  </div>
                  <div className="text-[8px] text-slate-500">{w.iso_week.split('-').pop()}</div>
                </div>
              );
            })}
          </div>
        )}
        {p.last_error_sample && (
          <div className="text-[10px] text-rose-300 mt-2 border border-rose-400/20 bg-rose-500/5 rounded p-2 font-mono"
                data-testid={`mi-stats-error-${p.platform}`}>
            {p.last_error_sample}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
